import type { TimelineItem } from '../react-calendar-timeline';
import { useDefaultCurrency } from '../../../state/zustand';
import { useViolationIndicatorBasisState } from '../../../state/planning/options';
import { mapKeyBy } from '@seaber/common/lib/collections';
import {
  CoreEventType,
  getEventLocationId,
  isCargoEvent,
  isDischargeEvent,
  isLoadEvent,
  isPortCallArrivalEventTimeData,
} from '../../core/TimeData';
import uniq from 'lodash/uniq';
import { getEventLocation } from '../../core/Core';
import { get } from '../../../lib/util';
import { getOrderContractType } from '../../order/OrderTableUtils';
import { hToMs, msToH } from '../../../lib/time';
import { Objective } from '../../core/suggestions/VoyageSuggestionParams';
import { Box, Button, Text, Tooltip, View } from '../../ui';
import { EventType } from '@seaber/common/graphql/types';
import { formatCurrency } from '../../../lib/format';
import { oneDecimal } from '../../shipment/useUpdatingDuration';
import { RedCircleIcon } from '../../Icons';
import React from 'react';
import type {
  IndicatorData,
  TimelineSuggestion,
  ViolationIndicatorData,
  VoyageAssortment,
} from '../types';
import {
  AssortmentExclusionsIndicator,
  indicatorTypeToColor,
  indicatorTypeToLineStyle,
  StaticLineIndicator,
} from '../Indicators';
import startCase from 'lodash/startCase';
import { ShipmentAssortmentsField } from '../ShipmentField';
import { useSingleClickHandlers } from '../utils/hooks';
import {
  SuggestionControlsButton,
  SuggestionControlsContainer,
} from './TimelineSuggestion.styled';

const showDevDetails =
  typeof window !== 'undefined' && window.location.search.includes('dev');
const isTimelineVoyageEstimatorEnabled =
  typeof window !== 'undefined' &&
  localStorage.getItem('timeline-voyage-estimator') === 'true';

const TimelineSuggestionImpl = (props: {
  item: TimelineItem<TimelineSuggestion>;
  pixelsPerMs: number;
  isMobileView?: boolean;
  selected?: boolean;
}) => {
  const baseCurrency = useDefaultCurrency();
  const [violationIndicatorBasis] = useViolationIndicatorBasisState();

  const {
    pixelsPerMs,
    item: {
      /** Note: not the same as suggestion.entries when the suggestion is expanded */
      entries,
      onSelect: onSuggestionAccept,
      onDismiss: onDismissSuggestion,
      suggestion,
      suggestionIndex,
      showInquiries,
      start,
      zIndex,
      vesselId,
      isBestFound,
      scheduleContext: { locations },
    },
    isMobileView,
  } = props;

  const clickHandlers = useSingleClickHandlers(
    !isTimelineVoyageEstimatorEnabled,
  );

  const schedule = suggestion.schedule;
  if (!schedule) return null;

  const { timeData, combination } = schedule;
  const etaTdByPortCallId = mapKeyBy(
    timeData.filter(isPortCallArrivalEventTimeData),
    (td) => td.event.portCallId,
  );

  const renderedAssignments = combination.assignments.filter((a) =>
    entries.some((e) => e.orderId === a.order.id),
  );
  // showKPIs when isFirstBoxInSuggestion
  const showKPIs = entries[0] === suggestion.entries[0];

  const newTimeDataIndices = new Set(
    renderedAssignments.flatMap((a) => [a.loadIndex, a.dischargeIndex]),
  );
  const newTimeData = timeData.filter((_td, i) => newTimeDataIndices.has(i));

  if (!newTimeData.every((td) => isCargoEvent(td.event))) {
    throw new Error(`Bad event type for TimelineSuggestion component`);
  }

  const assortmentExclusionsViolations = uniq(
    newTimeData.flatMap((td) => td.assortmentExclusions),
  );

  const indicators: Array<IndicatorData> = [];
  const violationIndicators: Array<ViolationIndicatorData> = [];

  const portCalls = newTimeData.reduce(
    (acc, td) => {
      const portName = getEventLocation(td.event, locations).name;
      const prevPortName = get(acc, acc.length - 1)?.portName;
      if (portName && portName !== prevPortName) {
        acc.push({ portName });
      }
      return acc;
    },
    [] as { portName: string }[],
  );

  const assortments: Array<VoyageAssortment> = [];

  newTimeData.forEach((td) => {
    const { event } = td;

    if (!isCargoEvent(event)) {
      throw new Error(
        `Bad event type for TimelineSuggestion component: ${event.category}`,
      );
    }

    const { start, end } = td;

    const port = getEventLocation(event, locations);

    const relatedOrder = event.order;
    if (event.type === CoreEventType.Load && relatedOrder) {
      assortments.push({
        assortmentName:
          relatedOrder.assortmentByAssortmentId?.name ?? 'Assortment name N/A',
        amount: relatedOrder.cargoAmount
          ? +relatedOrder.cargoAmount * td.amount
          : 0,
        amountUnit: relatedOrder.cargoUnitByCargoUnitId?.name || 'mts',
        incomingContractType: getOrderContractType(relatedOrder),
        cargoDeckOption:
          relatedOrder.orderDetailsByOrderId.nodes[0]?.cargoDeckOption ?? null,
      });
    }

    // Actual event
    indicators.push({
      type: event.type === CoreEventType.Load ? 'load' : 'discharge',
      subtype: undefined,
      duration: end - start,
      startTime: start,
      portName: port?.name ?? undefined,
      timezone: port?.timezone ?? undefined,
      assortmentName: relatedOrder?.assortmentByAssortmentId?.name ?? undefined,
    });

    const cancellingOrDeliveryViolation =
      violationIndicatorBasis === 'none'
        ? 0
        : violationIndicatorBasis === 'eta'
          ? td.cancellingOrDeliveryViolationVsEta
          : td.cancellingOrDeliveryViolationVsOperations;

    if (
      (isLoadEvent(event) || isDischargeEvent(event)) &&
      cancellingOrDeliveryViolation !== 0
    ) {
      const etaTd = etaTdByPortCallId.get(event.portCallId);
      const operationsStart = td.operationsStart ?? start;
      const startTime =
        violationIndicatorBasis === 'eta'
          ? (etaTd?.start ?? operationsStart)
          : operationsStart;
      violationIndicators.push({
        type:
          event.type === CoreEventType.Load
            ? 'load_violation'
            : 'discharge_violation',
        startTime,
        duration: hToMs(cancellingOrDeliveryViolation),
        portName: port?.name ?? '',
        locationId: getEventLocationId(event),
        voyageCharterId:
          relatedOrder.incomingVoyageCharterRelation?.voyageCharterId,
      });
    }
  });

  const missingIncomeData =
    (suggestion.objective === Objective.MaximizeVCP ||
      suggestion.objective === Objective.MaximizeTCE) &&
    suggestion.missingIncomeOrderIds.size
      ? 'Missing freight for order ids: ' +
        Array.from(suggestion.missingIncomeOrderIds).join(', ')
      : null;

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        ...(isMobileView
          ? {
              display: 'grid',
              gridTemplateRows: 'auto auto',
              alignItems: 'start',
              gap: '8px',
            }
          : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }),
        ...(props.selected && {
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: 'primary.blue_200',
        }),
      }}
    >
      <Box width={1} height="100%" bg="neutral.white_50" {...clickHandlers}>
        <Box display="flex" flexDirection="column" height="100%">
          <View
            flexDirection="row"
            justifyContent="space-between"
            bg={'orderStatus.suggested'}
            overflow="hidden"
            height="20px"
            alignItems="center"
          >
            {portCalls.map((portCall, index) => (
              <Text
                key={index}
                isUnselectable
                isSingleLine
                fontSize={2}
                color="neutral.white_50"
                overflow="hidden"
                pl={2}
              >
                {portCall.portName}
              </Text>
            ))}
          </View>
          <View
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            height="18px"
          >
            <AssortmentExclusionsIndicator
              disableTooltip={false}
              assortmentExclusions={assortmentExclusionsViolations}
            />
            <ShipmentAssortmentsField
              assortments={assortments}
              showInquiries={showInquiries}
            />
          </View>
          <View height="4px" width={1} bg="neutral.white_50" />
        </Box>
        {/* Bottom indicators */}
        <Box
          display="flex"
          flexDirection="row"
          position="absolute"
          bottom={0}
          height="4px"
          zIndex={zIndex}
        >
          {indicators.map((indicator, index) => (
            <StaticLineIndicator
              key={index}
              color={
                indicator.type === 'single' &&
                indicator.subtype === EventType.WAITING
                  ? 'orange.orange_600'
                  : indicatorTypeToColor[indicator.type]
              }
              offset={(indicator.startTime - start) * pixelsPerMs}
              width={indicator.duration * pixelsPerMs}
              lineStyle={indicatorTypeToLineStyle[indicator.type]}
              tooltip={{
                title:
                  startCase(
                    indicator.subtype?.toLowerCase() ?? indicator.type,
                  ) + (indicator.portName ? ` at ${indicator.portName}` : ''),
                startTime: indicator.startTime,
                duration: msToH(indicator.duration),
                portName: indicator.portName,
                timezone: indicator.timezone,
                assortmentName: indicator.assortmentName,
                from: indicator.from,
                to: indicator.to,
              }}
            />
          ))}
        </Box>
        <Box position="absolute" bottom="20px" height="4px">
          {violationIndicators.map((indicator, index) => (
            <StaticLineIndicator
              key={index}
              color="voyageEvents.violation"
              opacity={0.7}
              width={Math.abs(indicator.duration * pixelsPerMs)}
              offset={
                (indicator.startTime -
                  start -
                  (indicator.duration > 0 ? indicator.duration : 0)) *
                pixelsPerMs
              }
              lineStyle="solid"
              tooltip={{
                title:
                  startCase(indicator.type) +
                  (indicator.portName ? ` in ${indicator.portName}` : ''),
                duration: msToH(indicator.duration),
                type: indicator.type,
              }}
            />
          ))}
        </Box>
      </Box>
      {/* Dismiss button */}
      <Box
        position={isMobileView ? 'relative' : 'absolute'}
        {...(!isMobileView && { left: '100%' })}
        bottom={showKPIs ? 3 : 0}
        ml="10px"
        width="max-content"
        backgroundColor="transparent"
      >
        <Button
          variant="transparent"
          size="xs"
          p={1}
          width="18px"
          height="18px"
          onPress={() => onDismissSuggestion(vesselId, suggestionIndex)}
          data-testid={`dismiss-suggestion-${vesselId}-${suggestionIndex}`}
          borderStyle="solid"
          alignItems="center"
        >
          X
        </Button>
      </Box>
      {showKPIs && (
        <Box
          position={isMobileView ? 'relative' : 'absolute'}
          borderWidth={1}
          borderStyle="dashed"
          borderColor="neutral.gray_700"
          onClick={() => {
            onSuggestionAccept(vesselId, suggestionIndex);
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
          }}
          width="max-content"
          {...(!isMobileView && { left: '100%' })}
          bottom={0}
          ml="10px"
        >
          <Tooltip
            content={
              <View>
                {missingIncomeData ? <Text>{missingIncomeData}</Text> : null}
                <Text>
                  Suggestion VCP{' '}
                  {Math.round(+suggestion.vesselPerformanceMetrics.delta.vcp)}{' '}
                  <i>
                    {suggestion.vesselPerformanceMetrics.delta.vcp.toString()}
                  </i>
                </Text>
                <Text>
                  Vessel VCP{' '}
                  {Math.round(
                    +suggestion.vesselPerformanceMetrics.newVoyage.vcp,
                  )}
                </Text>
                <Text>
                  Voyage VCP{' '}
                  {Math.round(
                    +suggestion.voyagePerformanceMetrics.newVoyage.vcp,
                  )}
                </Text>
                {suggestion.voyagePerformanceMetrics.originalVoyage.vcp ? (
                  <Text>
                    Voyage VCP Δ{' '}
                    {Math.round(
                      +suggestion.voyagePerformanceMetrics.newVoyage.vcp -
                        +suggestion.voyagePerformanceMetrics.originalVoyage.vcp,
                    )}
                  </Text>
                ) : null}
                <Text>Ballast Δ {Math.round(suggestion.ballastDelta)}nm</Text>
                <Text>Delay Δ {Math.round(suggestion.delayDelta)}h</Text>
                <Text>Duration Δ {Math.round(suggestion.durationDelta)}h</Text>
                <Text>
                  Mass Δ{' '}
                  {Math.round(
                    suggestion.vesselPerformanceMetrics.delta.totalMass,
                  )}
                  mts
                </Text>
                <Text>Distance Δ {Math.round(suggestion.distanceDelta)}nm</Text>
                <Text>
                  Bunker cost Δ {Math.round(suggestion.bunkerCostDelta)}€
                </Text>
                <Text>
                  Voyage port cost {Math.round(suggestion.voyagePortCost)}€
                </Text>
                <Text>Port cost Δ {Math.round(suggestion.portCostDelta)}€</Text>
                <Text>
                  Cost {formatCurrency(suggestion.cost, 0, baseCurrency)}
                </Text>
                <Text>
                  Income {formatCurrency(suggestion.income, 0, baseCurrency)}
                </Text>
                <Text>Days {oneDecimal(suggestion.durationInDays)}</Text>
                {showDevDetails && (
                  <>
                    <Text>
                      Totals{' '}
                      <pre>
                        {JSON.stringify(suggestion.schedule?.totals, null, 2)}
                      </pre>
                    </Text>
                    <Text>
                      Inputs{' '}
                      <pre>
                        {JSON.stringify(suggestion.schedule?.inputs, null, 2)}
                      </pre>
                    </Text>
                  </>
                )}
              </View>
            }
          >
            <View
              bg={`rgb(242, 244, 246)`}
              borderRadius={4}
              px={3}
              py={2}
              style={suggestionShadowStyles}
              borderWidth={2}
              borderStyle="solid"
              borderColor={isBestFound ? '#5fad09' : 'transparent'}
              flexDirection="row"
              alignItems="center"
              data-testid={
                isBestFound
                  ? 'best-suggestion'
                  : 'suggestion-' + vesselId + '-' + suggestionIndex
              }
            >
              <Text color="text" fontWeight="bold" data-testid="objective">
                {suggestion.objective === Objective.MinimizeCost ? (
                  formatCurrency(suggestion.cost, 0, baseCurrency)
                ) : suggestion.objective === Objective.MaximizeVCP ? (
                  // in mobile view rendering TCE and Delta TCE in column
                  // and not rendering VCP
                  isMobileView ? (
                    <View flexDirection="column">
                      <Text>
                        {`TCE: ${formatCurrency(suggestion.tce, 0, baseCurrency)}/d`}
                      </Text>
                      {suggestion.tceDelta !== null && (
                        <Text>
                          {`TCEΔ: ${formatCurrency(suggestion.tceDelta, 0, baseCurrency)}/d`}
                        </Text>
                      )}
                    </View>
                  ) : (
                    // in other views rendering VCP, TCE and Delta TCE in one row
                    `VCP: ${Math.round(
                      +suggestion.vesselPerformanceMetrics.delta.vcp,
                    )} TCE: ${
                      formatCurrency(suggestion.tce, 0, baseCurrency) + '/d'
                    }${
                      suggestion.tceDelta !== null
                        ? ` TCEΔ: ${
                            formatCurrency(
                              suggestion.tceDelta,
                              0,
                              baseCurrency,
                            ) + '/d'
                          }`
                        : ''
                    }`
                  )
                ) : suggestion.objective === Objective.MaximizeTCE ? (
                  // in mobile view rendering TCE and Delta TCE in column
                  // and not rendering VCP
                  isMobileView ? (
                    <View flexDirection="column">
                      <Text>
                        {`TCE: ${formatCurrency(suggestion.tce, 0, baseCurrency)}/d`}
                      </Text>
                      {suggestion.tceDelta !== null && (
                        <Text>
                          {`TCEΔ: ${formatCurrency(suggestion.tceDelta, 0, baseCurrency)}/d`}
                        </Text>
                      )}
                    </View>
                  ) : (
                    // in other views rendering VCP, TCE and Delta TCE in one row
                    `TCE: ${
                      formatCurrency(suggestion.tce, 0, baseCurrency) + '/d'
                    }${
                      suggestion.tceDelta !== null
                        ? ` TCEΔ: ${
                            formatCurrency(
                              suggestion.tceDelta,
                              0,
                              baseCurrency,
                            ) + '/d'
                          }`
                        : ''
                    } VCP: ${Math.round(
                      +suggestion.vesselPerformanceMetrics.delta.vcp,
                    )}`
                  )
                ) : suggestion.objective === Objective.ShortestDistance ? (
                  Math.round(suggestion.distanceDelta)
                ) : (
                  Math.round(suggestion.ballastDelta)
                )}
              </Text>
              {missingIncomeData ? <RedCircleIcon /> : null}
            </View>
          </Tooltip>
        </Box>
      )}
    </div>
  );
};

export const suggestionShadowStyles = {
  boxShadow: '0px 2px 2.62px rgba(0, 0, 0, 0.23)',
};
export const TimelineSuggestionRenderer = React.memo(TimelineSuggestionImpl);

interface SuggestionControlsProps {
  clearSuggestions: () => void;
  showSuggestionReport: () => void;
  onOpenEstimator?: () => void;
  hasSelectedSuggestions?: boolean;
}

export const SuggestionControls = ({
  clearSuggestions,
  showSuggestionReport,
  onOpenEstimator,
  hasSelectedSuggestions = false,
}: SuggestionControlsProps) => {
  return (
    <SuggestionControlsContainer>
      <SuggestionControlsButton onPress={clearSuggestions}>
        Dismiss suggestions
      </SuggestionControlsButton>
      <SuggestionControlsButton onPress={showSuggestionReport}>
        Show report
      </SuggestionControlsButton>
      {onOpenEstimator && hasSelectedSuggestions && (
        <SuggestionControlsButton onPress={onOpenEstimator}>
          Open estimator with selected suggestions
        </SuggestionControlsButton>
      )}
    </SuggestionControlsContainer>
  );
};
